import type { ApiAdmininsightscompaniesGetCollectionParams } from '@r/api/generated/client.schemas.ts';
import { useApiAdmininsightscompaniesGetCollection } from '@r/api/generated/client.ts';
import type { InsightCompanyInsightCompanyReadModel } from '@r/api/generated/client.schemas.ts';
import { DataBrickSkeleton } from '@r/components/kpi/skeleton/data-brick-skeleton.tsx';
import { AwardIcon, FactoryIcon, PercentCircleIcon, PieChartIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getDataBrick } from '@admin/components/data-brick.tsx';
import {
  processCompaniesByStepsResults,
  CompaniesByStepsPieChartBloc,
  type CompaniesByStepsRenderingData
} from '@admin/components/steps/companies-by-steps-pie-chart-bloc.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '@ui/card.tsx';
import { PieChartSkeleton } from '@r/components/kpi/skeleton/pie-chart-skeleton.tsx';
import { useState } from 'react';
import type { Option } from '@r/components/kpi/filters/select-tag-input.tsx';
import { KpiFilterBar } from '@r/components/kpi/filters/kpi-filter-bar.tsx';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { LogicTypeEnum, companyStatuses } from '@r/types/index.ts';
import { isStepLogicWorkspace } from '@r/utils/workspace-utils.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type CompanyInsights = InsightCompanyInsightCompanyReadModel;

export default function CompanyInsights() {
  const { t } = useTranslation();
  const user = useUser();
  const { workspace, isWorkspaceSuccess } = UseWorkspace(user.data);
  const workspaceLogicType = workspace?.currentLogicWorkspace?.logic.type;

  let companiesBySteps: CompaniesByStepsRenderingData[] = [];

  const [companyStatus, setCompanyStatusFilterValue] = useState<Option[]>([
    { id: 'company.status.registered', name: t('company.status.registered') }
  ]);
  const [companyTypologies, setCompanyTypologiesFilterValue] = useState<Option[]>([]);
  const [selectedSalesIds, setSelectedSalesIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ from?: string; to?: string }>({});

  const statusIds = companyStatus
    .map((status) => String(status.id))
    .filter((id): id is (typeof companyStatuses)[number] =>
      companyStatuses.includes(id as (typeof companyStatuses)[number])
    );

  const typologyNames = companyTypologies.map((typology) => String(typology.id));

  const queryParams: ApiAdmininsightscompaniesGetCollectionParams = {
    ...(statusIds.length > 0 ? { 'status[]': statusIds } : {}),
    ...(typologyNames.length > 0 ? { 'typology[]': typologyNames } : {}),
    ...(selectedSalesIds.length > 0 ? { 'sales[]': selectedSalesIds } : {}),
    ...(dateRange.from ? { from: dateRange.from } : {}),
    ...(dateRange.to ? { to: dateRange.to } : {})
  };

  const companyInsightsQuery = useApiAdmininsightscompaniesGetCollection(queryParams);
  useOrvalQueryErrorToast(companyInsightsQuery, t('toast.error.label'));

  const getTotalCompaniesByStatus = (companyInsights: CompanyInsights): number => {
    const registeredValue = companyStatuses.find((v) => v.includes('registered'));

    const hasOnlyRegistered =
      statusIds.length === 1 && registeredValue && statusIds[0] === registeredValue;

    return hasOnlyRegistered
      ? companyInsights.totalCompaniesRegistered
      : companyInsights.totalCompanies;
  };

  function getRegisteredCompaniesPercentage(companyInsights: CompanyInsights): number {
    if (!companyInsights.totalCompanies || !companyInsights.totalCompaniesRegistered) {
      return 0;
    }

    return Math.round(
      (companyInsights.totalCompaniesRegistered / companyInsights.totalCompanies) * 100
    );
  }

  function calculateAverageStepReached(companyInsights: CompanyInsights): string {
    const noStep = t('company.no.step');
    const totalCompaniesByStatus = getTotalCompaniesByStatus(companyInsights);

    if (!totalCompaniesByStatus) {
      return noStep;
    }

    let companiesCount = 0;

    for (const step of companiesBySteps) {
      companiesCount += step.totalCompanies;

      if (companiesCount >= totalCompaniesByStatus / 2) {
        return step.stepName;
      }
    }

    return noStep;
  }

  if (
    isWorkspaceSuccess &&
    isStepLogicWorkspace(workspace) &&
    companyInsightsQuery.isSuccess &&
    companyInsightsQuery.data
  ) {
    const data = companyInsightsQuery.data;

    companiesBySteps = processCompaniesByStepsResults(
      getTotalCompaniesByStatus(data),
      data.companiesPerStep,
      t('company.no.step')
    );
  }

  return (
    <>
      <KpiFilterBar
        onStatusChange={setCompanyStatusFilterValue}
        onTypologyChange={setCompanyTypologiesFilterValue}
        companyTypologies={companyTypologies}
        onSalesChange={setSelectedSalesIds}
        initialSelectedStatuses={[
          { id: 'company.status.registered', name: t('company.status.registered') }
        ]}
        onDateRangeChange={(range) => {
          setDateRange(range);
        }}
        dateRange={dateRange}
      />

      <div className="flex flex-wrap space-y-5 grow xl:flex-nowrap xl:space-x-5 xl:space-y-0">
        <div className="flex flex-col space-y-5 w-full xl:w-6/12 xl:min-h-full">
          <div className="w-full">
            {(companyInsightsQuery.isLoading || companyInsightsQuery.isError) && (
              <DataBrickSkeleton size={'large'} />
            )}

            {companyInsightsQuery.isSuccess &&
              companyInsightsQuery.data &&
              companyInsightsQuery.data.totalCompanies !== null && (
                <div className="flex flex-wrap space-x-0 space-y-5 w-full 2xl:flex-nowrap 2xl:space-x-5 2xl:space-y-0">
                  {getDataBrick(
                    t('admin.kpi.total.companies'),
                    <FactoryIcon />,
                    companyInsightsQuery.data.totalCompanies,
                    ` ${t('common.companies')}`,
                    '',
                    null,
                    'large'
                  )}
                </div>
              )}
          </div>

          {workspaceLogicType === LogicTypeEnum.Step && (
            <div className="flex flex-wrap w-full">
              {(companyInsightsQuery.isLoading || companyInsightsQuery.isError) && (
                <div className="flex w-full flex-wrap space-x-0 space-y-5 p-2.5 pr-0 xl:pr-2.5 2xl:flex-nowrap 2xl:space-x-5 2xl:space-y-0">
                  <DataBrickSkeleton />
                  <DataBrickSkeleton />
                </div>
              )}

              {companyInsightsQuery.isSuccess && companyInsightsQuery.data && (
                <div className="flex flex-wrap space-x-0 space-y-5 w-full 2xl:flex-nowrap 2xl:space-x-5 2xl:space-y-0">
                  {getDataBrick(
                    t('admin.kpi.median.step.reached'),
                    <AwardIcon />,
                    calculateAverageStepReached(companyInsightsQuery.data),
                    null,
                    t('admin.kpi.median.step.reached.info')
                  )}

                  {getDataBrick(
                    t('admin.kpi.registered.companies.percent'),
                    <PercentCircleIcon />,
                    getRegisteredCompaniesPercentage(companyInsightsQuery.data),
                    ' %'
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {isStepLogicWorkspace(workspace) && (
          <div className="w-full xl:w-6/12">
            <Card className="flex flex-col">
              <CardHeader className="space-y-0">
                <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
                  <PieChartIcon />
                  <div className="text-xl font-bold">{t('admin.kpi.total.companies.by.step')}</div>
                </CardTitle>
              </CardHeader>

              <CardContent className="grow">
                {(companyInsightsQuery.isLoading || companyInsightsQuery.isError) && (
                  <PieChartSkeleton />
                )}
                {companyInsightsQuery.isSuccess && companiesBySteps.length > 0 && (
                  <CompaniesByStepsPieChartBloc chartData={companiesBySteps} />
                )}
                {companyInsightsQuery.isSuccess && companiesBySteps.length === 0 && (
                  <div>
                    <PieChartSkeleton breathe={false} />
                    <p className="text-center text-foreground">{t('common.data.empty')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
